import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  alpha,
  styled,
  PaperProps,
  Link,
  Grid
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  CheckCircle,
  Error,
  Close
} from '@mui/icons-material';

import { callAPI, getLocalValue, LoacalVariables, Method } from '../../commonFunctions/CommonFunctions';
import ExcelStructureModal from '../components/excelStructurModal';
import { useAuth } from '../../StateManagement/authContext';
import WSSelectField from '../../Components/Select/WSSelect';
import { useForm } from 'react-hook-form';
import { useSnackbar } from '../../Components/Snackbar/SnackBarContext';

interface DropZoneProps extends PaperProps {
  isDragActive: boolean;
}

// Styled drop zone component
const DropZone = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isDragActive', // VERY IMPORTANT: prevent isDragActive from being passed to DOM
})<DropZoneProps>(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragActive
    ? alpha(theme.palette.primary.light, 0.1)
    : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.light, 0.05),
  },
  marginBottom: theme.spacing(2),
}));

const UploadLoans: React.FC = () => {
  const { user: userData } = useAuth();
  const userType = getLocalValue(LoacalVariables.UserType);
  const { control, reset, watch, setValue, getValues } = useForm();
  const { showMessage } = useSnackbar();

  const [loadingRegion, setLoadingRegion] = useState(false);
  const [districtOptions, setDistrictOptions] = useState<any[]>([]);
  const [blockOptions, setBlockOptions] = useState<any[]>([]);
  const [cdsOptions, setCdsOptions] = useState<any[]>([]);

  const selectedDistrict = watch("districtId");
  const selectedBlock = watch("blockId");

  useEffect(() => {
    if (userType === 'block') {
      setValue('role', 'cds');
    }
  }, [])

  const fetchDistricts = async () => {
    try {
      const res = await callAPI("districts/getAll", Method.GET);
      setDistrictOptions(
        res?.data?.map((d: any) => ({ label: d.name, value: d._id })) || []
      );
    } catch (error) {
      console.error("Failed to fetch districts:", error);
      showMessage("Failed to load districts", "error");
    }
  };

  useEffect(() => {
    if (selectedDistrict) {
      fetchBlocks(selectedDistrict);
      reset({ blockId: "", cdsId: "" }, { keepValues: true }); // clear lower levels
      setCdsOptions([]); // clear CDS options
    }
  }, [selectedDistrict]);

  useEffect(() => {
    if (userType === 'admin' && selectedBlock) {

      fetchCDS(selectedBlock);
      reset({ cdsId: "" }, { keepValues: true }); // clear CDS on block change
    } else if (userType === 'block' && userData?.block) {
      fetchCDS(userData.block);
    }
  }, [selectedBlock]);

  const fetchBlocks = async (districtId: string) => {
    try {
      setLoadingRegion(true);
      const res = await callAPI(`blocks/byDistrict/${districtId}`, Method.GET);
      setBlockOptions(
        res?.data?.map((b: any) => ({ label: b.name, value: b._id })) || []
      );
    } catch (error) {
      console.error("Failed to fetch blocks:", error);
      showMessage("Failed to load blocks", "error");
    } finally {
      setLoadingRegion(false);
    }
  };

  const fetchCDS = async (blockId: string) => {
    try {
      const res = await callAPI(`cds/byBlock/${blockId}`, Method.GET);

      setCdsOptions(
        res?.map((c: any) => ({ label: c.name, value: c._id })) || []
      );
    } catch (error) {
      console.error("Failed to fetch CDS:", error);
      showMessage("Failed to load CDS", "error");
    }
  };


  useEffect(() => {
    if (userType === "admin") {
      fetchDistricts();
    } else if (userType === "district") {
      const districtId = getLocalValue(LoacalVariables.District);
      if (districtId) {
        reset((prev) => ({
          ...prev,
          districtId,
        }), { keepValues: true });
        fetchBlocks(districtId);
      }
    }
  }, []);


  useEffect(() => {
    const role = watch("role");
    if (role === "admin") {
      reset({ districtId: "", blockId: "", cdsId: "" }, { keepValues: true });
    } else if (role === "district") {
      reset({ blockId: "", cdsId: "" }, { keepValues: true });
    } else if (role === "block") {
      reset({ cdsId: "" }, { keepValues: true });
    }
  }, [watch("role")]);

  const [openFormat, setOpenFormat] = useState(false)
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (file: File) => {
    if (file) {
      // Check if file is Excel format
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError("Please select an Excel file (.xlsx or .xls)");
        return;
      }

      setFile(file);
      setMessage(null);
      setError(null);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  }, []);

  const handleUpload = async () => {
    if (!file) {
      setError("Please select an Excel file first.");
      return;
    }

    try {
      setLoading(true);
      setMessage(null);
      setError(null);

      const formValues = getValues(); // get form values

      const formData = new FormData();

      formData.append("file", file);

      // append region data

      if (formValues.districtId)
        formData.append("districtId", formValues.districtId);

      if (formValues.blockId)
        formData.append("blockId", formValues.blockId);

      if (formValues.cdsId)
        formData.append("cdsId", formValues.cdsId);

      const res = await callAPI(
        "loans/upload-excel",
        Method.POST,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setMessage(res.message || "Upload successful ✅");
      setFile(null);

    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || "Upload failed ❌");
    } finally {
      setLoading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const roleOptions = [
    { label: "District", value: "district" },
    { label: "Block", value: "block" },
    { label: "CDS", value: "cds" },
  ]

  const getCreatableRoles = (userType: string) => {
    switch (userType) {
      case "admin":
        return ["district", "block", "cds"];
      case "district":
        return ["cds"];
      case "block":
        return ["cds"];
      default:
        return [];
    }
  };

  const allowedRoles = getCreatableRoles(getLocalValue(LoacalVariables.UserType));
  const filteredRoleOptions = roleOptions.filter((role) =>
    allowedRoles.includes(role.value)
  );

  return (
    <Box display="flex" justifyContent="center" mt={4} px={2}>
      <Card sx={{ width: "100%", maxWidth: 800, borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
            Upload Loan Data
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Upload Excel files containing old Kudumbashree loan data. Only <b>.xlsx</b> or <b>.xls</b> files are supported.
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Need help with format?{" "}
            <Link
              component="button"
              variant="body2"
              onClick={() => setOpenFormat(true)}
              underline="hover"
              sx={{ fontWeight: 500 }}
            >
              View Excel Structure
            </Link>
          </Typography>

          <ExcelStructureModal open={openFormat} setOpen={setOpenFormat} />

          {userType !== 'cds' && <Grid item xs={12}>

            <Box sx={{
              p: 3,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.03)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              <Grid container spacing={2}>
                {getLocalValue(LoacalVariables.UserType) === "admin" && <Grid item xs={12}>
                  <WSSelectField
                    control={control}
                    id="role"
                    name="role"
                    label="Select Role *"
                    rules={{ required: true }}
                    options={filteredRoleOptions}
                    disabled={loading}
                  />
                </Grid>}

                {/* Conditional Location Fields */}
                {["district", "block", "cds"].includes(watch("role")) &&
                  getLocalValue(LoacalVariables.UserType) === "admin" && (
                    <Grid item xs={12} md={6}>
                      <WSSelectField
                        control={control}
                        id="districtId"
                        name="districtId"
                        label="Select District *"
                        rules={{ required: true }}
                        options={districtOptions}
                      />
                    </Grid>
                  )}

                {["block", "cds"].includes(watch("role")) && userType !== 'block' && (
                  <Grid item xs={12} md={6}>
                    <WSSelectField
                      control={control}
                      id="blockId"
                      name="blockId"
                      label="Select Block *"
                      rules={{ required: true }}
                      loader={loadingRegion}
                      options={blockOptions}
                    />
                  </Grid>
                )}

                {watch("role") === "cds" && (
                  <Grid item xs={12}>
                    <WSSelectField
                      control={control}
                      id="cdsId"
                      name="cdsId"
                      label="Select CDS *"
                      rules={{ required: true }}
                      options={cdsOptions}
                      loader={loadingRegion}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          }

          <Box mt={3}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => e.target.files && handleFileChange(e.target.files[0])}
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="excel-file-input"
            />

            <DropZone
              isDragActive={isDragActive}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Drag & Drop your Excel file
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or <span style={{ color: '#1976d2', textDecoration: 'underline' }}>browse files</span> from your computer
              </Typography>
            </DropZone>

            {file && (
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', mb: 2 }}>
                <InsertDriveFile sx={{ mr: 2, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <Chip
                  icon={<CheckCircle />}
                  label="Selected"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Button size="small" onClick={removeFile}>
                  <Close />
                </Button>
              </Paper>
            )}

            {loading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading file...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {message && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}

            {error && (
              <Alert severity="error" icon={<Error />} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={<CloudUpload />}
              onClick={handleUpload}
              disabled={!file || loading}
              sx={{ py: 1.5 }}
            >
              {loading ? "Uploading..." : "Process Loan Data"}
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              The system will validate and process your Excel file automatically
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UploadLoans;